import * as React from "react";
import { useTl, useApiService } from "../../../../hooks";
import { PermissionApi, UserProfileActionSearch, UserProfileActionGridDto, EPermission } from "../../../../api";
import { useGridState, useSearchApi, DataTable } from "nsitools-react";
import { ETLCodes } from "../../../../locales";
import { Radio } from "@blueprintjs/core";

export interface IPermissionGridProps {
  userProfileId: number;
}

export const PermissionGrid: React.FunctionComponent<IPermissionGridProps> = ({ userProfileId }) => {
  const { t } = useTl();
  const api = useApiService(PermissionApi);
  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5, 15, 25],
    pageSize: 25,
    sortKeys: { module: "ASC" },
  });
  const searchFunction = React.useCallback(
    (sObj?: UserProfileActionSearch) => {
      sObj.userProfileId = userProfileId;
      return api.baseSearch(sObj);
    },
    [api, userProfileId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: false,
    trimSearchObjectStringValues: true,
  });
  const { setData, data } = tableState;

  React.useEffect(() => {
    if (userProfileId) {
      search();
    }
  }, [search, userProfileId]);

  const changePermission = React.useCallback(
    (value: EPermission, row: UserProfileActionGridDto) => {
      const dataToSet = [...data];
      const index = dataToSet.findIndex((d) => d.actionId === row.actionId && d.userProfileId === row.userProfileId);
      dataToSet[index]["permission"] = value;
      dataToSet[index]["userProfileId"] = userProfileId;
      setData(dataToSet);
      api.savePermission(dataToSet[index]);
    },
    [api, data, setData, userProfileId]
  );

  const columns = React.useMemo(
    () => [
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.Module),
        fieldName: "module",
        autoFitContent: true,
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.Tab),
        fieldName: "tab",
        autoFitContent: true,
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.None),
        fieldName: "permissionNone",
        autoFitContent: true,
        alignment: "center" as any,
        render: (row: UserProfileActionGridDto) => (
          <Radio
            checked={!row.permission || row.permission === EPermission.NONE}
            onChange={(e) => changePermission(EPermission.NONE, row)}
          />
        ),
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.Read),
        fieldName: "permissionR",
        autoFitContent: true,
        alignment: "center" as any,
        render: (row: UserProfileActionGridDto) => (
          <Radio
            checked={row.permission === EPermission.R}
            disabled={row.executable}
            onChange={(e) => changePermission(EPermission.R, row)}
          />
        ),
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.ReadWrite),
        fieldName: "permissionRW",
        autoFitContent: true,
        alignment: "center" as any,
        render: (row: UserProfileActionGridDto) => (
          <Radio
            checked={row.permission === EPermission.RW}
            disabled={row.executable}
            onChange={(e) => changePermission(EPermission.RW, row)}
          />
        ),
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.ReadWriteDelete),
        fieldName: "permissionRWD",
        autoFitContent: true,
        alignment: "center" as any,
        render: (row: UserProfileActionGridDto) => (
          <Radio
            checked={row.permission === EPermission.RWD}
            onChange={(e) => changePermission(EPermission.RWD, row)}
            disabled={row.executable}
          />
        ),
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.Execute),
        fieldName: "permissionE",
        autoFitContent: true,
        alignment: "center" as any,
        render: (row: UserProfileActionGridDto) => (
          <Radio
            checked={row.permission === EPermission.E}
            onChange={(e) => changePermission(EPermission.E, row)}
            disabled={!row.executable}
          />
        ),
      },
    ],
    [changePermission, t]
  );

  return (
    <>
      <DataTable dateFormat="DD/MM/YYYY" tableState={tableState} loading={loading} columns={columns} />
    </>
  );
};
